import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';
import AuthBrandingPanel from '../../components/ui/AuthBrandingPanel';
import apiClient from '../../lib/apiClient';

const STEP_ORDER = ['request', 'verify', 'reset', 'success'];

const stepLabels = {
  request: 'Request Code',
  verify: 'Verify Code',
  reset: 'Set New Password',
  success: 'Password Updated',
};

const initialFormState = {
  email: '',
  code: '',
  password: '',
  confirmPassword: '',
};

const PasswordResetPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [step, setStep] = useState('request');
  const [form, setForm] = useState(initialFormState);
  const [errors, setErrors] = useState({});
  const [statusMessage, setStatusMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendDisabled, setResendDisabled] = useState(false);

  useEffect(() => {
    const stateEmail =
      typeof location?.state?.email === 'string' && location.state.email
        ? location.state.email
        : null;

    const storedEmail = (() => {
      try {
        const raw = localStorage.getItem('rememberedEmail');
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return typeof parsed?.email === 'string' ? parsed.email : null;
      } catch {
        return null;
      }
    })();

    const nextEmail = stateEmail ?? storedEmail;

    if (nextEmail) {
      setForm((prev) => ({ ...prev, email: nextEmail }));
    }
  }, [location]);

  useEffect(() => {
    let timeoutId;
    if (resendDisabled) {
      timeoutId = setTimeout(() => setResendDisabled(false), 30_000);
    }
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [resendDisabled]);

  const activeStepIndex = useMemo(() => STEP_ORDER.indexOf(step), [step]);

  const handleInputChange = (event) => {
    const { name, value } = event?.target ?? {};
    setForm((prev) => ({
      ...prev,
      [name]: name === 'code' ? value.replace(/\D/g, '').slice(0, 6) : value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
    if (errors.general) {
      setErrors((prev) => ({ ...prev, general: null }));
    }
    setStatusMessage('');
  };

  const validateEmail = () => {
    const validationErrors = {};
    if (!form.email) {
      validationErrors.email = 'Please enter the email associated with your account.';
    } else if (!/\S+@\S+\.\S+/.test(form.email)) {
      validationErrors.email = 'Enter a valid email address.';
    }
    return validationErrors;
  };

  const validateCode = () => {
    const validationErrors = {};
    if (!form.code || form.code.length !== 6) {
      validationErrors.code = 'Enter the six digit code from your email.';
    }
    return validationErrors;
  };

  const validatePasswords = () => {
    const validationErrors = {};
    if (!form.password || form.password.length < 8) {
      validationErrors.password = 'Password must be at least 8 characters long.';
    }
    if (!form.confirmPassword) {
      validationErrors.confirmPassword = 'Confirm your new password.';
    } else if (form.password !== form.confirmPassword) {
      validationErrors.confirmPassword = 'Passwords do not match.';
    }
    return validationErrors;
  };

  const handleRequestCode = async (event) => {
    event?.preventDefault();
    const validationErrors = validateEmail();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);
    setErrors({});
    setStatusMessage('');

    try {
      await apiClient.post('/auth/password-reset/request', { email: form.email });
      setStep('verify');
      setStatusMessage(
        'If the email is registered, we sent you a six digit verification code.',
      );
      setResendDisabled(true);
    } catch (error) {
      const message =
        error?.response?.data?.message ??
        'We could not start the password reset process. Try again in a moment.';
      setErrors({ general: Array.isArray(message) ? message.join('. ') : message });
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (event) => {
    event?.preventDefault();
    const validationErrors = {
      ...validateEmail(),
      ...validateCode(),
    };

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);
    setErrors({});
    setStatusMessage('');

    try {
      await apiClient.post('/auth/password-reset/verify', {
        email: form.email,
        code: form.code,
      });
      setStep('reset');
      setStatusMessage('Code verified. Create a new password to finish.');
    } catch (error) {
      const message =
        error?.response?.data?.message ??
        'The code is invalid or expired. Request a new one to continue.';
      setErrors({ general: Array.isArray(message) ? message.join('. ') : message });
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteReset = async (event) => {
    event?.preventDefault();
    const validationErrors = {
      ...validateEmail(),
      ...validateCode(),
      ...validatePasswords(),
    };

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);
    setErrors({});
    setStatusMessage('');

    try {
      await apiClient.post('/auth/password-reset/complete', {
        email: form.email,
        code: form.code,
        password: form.password,
      });
      setStep('success');
      setStatusMessage('Password updated successfully. You can now sign in with your new password.');
    } catch (error) {
      const message =
        error?.response?.data?.message ??
        'We could not reset your password. Confirm the code and try again.';
      setErrors({ general: Array.isArray(message) ? message.join('. ') : message });
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (resendDisabled || !form.email) {
      return;
    }
    setLoading(true);
    setErrors({});
    setStatusMessage('');
    try {
      await apiClient.post('/auth/password-reset/request', { email: form.email });
      setStatusMessage('We sent a new verification code if the email is registered.');
      setResendDisabled(true);
    } catch (error) {
      const message =
        error?.response?.data?.message ??
        'Unable to resend the code right now. Try again soon.';
      setErrors({ general: Array.isArray(message) ? message.join('. ') : message });
    } finally {
      setLoading(false);
    }
  };

  const handleBackToEmail = () => {
    setStep('request');
    setForm((prev) => ({
      ...prev,
      code: '',
      password: '',
      confirmPassword: '',
    }));
    setErrors({});
    setStatusMessage('');
  };

  const renderStepContent = () => {
    if (step === 'request') {
      return (
        <form onSubmit={handleRequestCode} className="space-y-6">
          <Input
            type="email"
            name="email"
            label="Work Email"
            placeholder="your.name@restaurant.com"
            value={form.email}
            onChange={handleInputChange}
            error={errors.email}
            disabled={loading}
            required
          />
          <Button type="submit" loading={loading} fullWidth iconName="Mail">
            Send reset code
          </Button>
        </form>
      );
    }

    if (step === 'verify') {
      return (
        <form onSubmit={handleVerifyCode} className="space-y-6">
          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-end gap-3">
              <div className="min-w-[200px] flex-1">
                <Input
                  type="text"
                  name="code"
                  label="Verification Code"
                  placeholder="Enter the 6 digit code"
                  value={form.code}
                  onChange={handleInputChange}
                  error={errors.code}
                  disabled={loading}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  required
                />
              </div>
              <Button
                type="submit"
                loading={loading}
                iconName="CheckCircle"
                className="sm:self-end"
              >
                Verify code
              </Button>
            </div>
            <div className="flex flex-wrap gap-3">
              <Button
                type="button"
                variant="ghost"
                iconName="ArrowLeft"
                onClick={handleBackToEmail}
                disabled={loading}
                className="w-full sm:w-auto"
              >
                Use a different email
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={handleResendCode}
                disabled={loading || resendDisabled}
                className="w-full sm:w-auto"
              >
                Resend code
              </Button>
            </div>
          </div>
        </form>
      );
    }

    if (step === 'reset') {
      return (
        <form onSubmit={handleCompleteReset} className="space-y-6">
          <Input
            type="password"
            name="password"
            label="New Password"
            placeholder="Enter a new password"
            value={form.password}
            onChange={handleInputChange}
            error={errors.password}
            disabled={loading}
            required
          />
          <Input
            type="password"
            name="confirmPassword"
            label="Confirm Password"
            placeholder="Re-enter your new password"
            value={form.confirmPassword}
            onChange={handleInputChange}
            error={errors.confirmPassword}
            disabled={loading}
            required
          />
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <Button
              type="button"
              variant="ghost"
              iconName="ArrowLeft"
              onClick={() => setStep('verify')}
              disabled={loading}
            >
              Back to code
            </Button>
            <Button type="submit" loading={loading} iconName="Lock">
              Update password
            </Button>
          </div>
        </form>
      );
    }

    return (
      <div className="space-y-6 text-center">
        <div className="flex justify-center">
          <div className="w-16 h-16 rounded-full bg-success/10 text-success flex items-center justify-center">
            <Icon name="CheckCircle2" size={32} />
          </div>
        </div>
        <div>
          <h2 className="text-2xl font-semibold text-foreground mb-2">Password reset complete</h2>
          <p className="text-muted-foreground">
            Sign in with your new password to get back to Ougama.
          </p>
        </div>
        <Button
          type="button"
          variant="default"
          iconName="LogIn"
          onClick={() => navigate('/login')}
          fullWidth
        >
          Back to sign in
        </Button>
      </div>
    );
  };

  return (
    <div className="auth-theme min-h-screen bg-background flex flex-col lg:flex-row">
      <AuthBrandingPanel>
        <div className="space-y-6 text-center lg:text-left">
          <p className="text-white/70 text-lg">
            Reset your credentials with confidence.
          </p>
          <div className="space-y-4">
            <div>
              <h3 className="text-white font-semibold">Step-by-step guidance</h3>
              <p className="text-white/70 text-sm">
                We walk you through requesting, verifying, and updating your password.
              </p>
            </div>
            <div>
              <h3 className="text-white font-semibold">Secure verification</h3>
              <p className="text-white/70 text-sm">
                Every reset is protected with email codes to keep your account safe.
              </p>
            </div>
          </div>
        </div>
      </AuthBrandingPanel>
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md bg-card border border-border rounded-2xl shadow-elevation-2 p-8 space-y-8">
          <div className="space-y-4">
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="inline-flex items-center text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
              <Icon name="ArrowLeft" size={18} className="mr-2" />
              Back to sign in
            </button>
            <div>
              <h1 className="text-3xl font-semibold text-foreground">Forgot password?</h1>
              <p className="text-muted-foreground mt-2">
                Follow the steps below to securely reset access to your account.
              </p>
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              {STEP_ORDER.map((stepKey, index) => (
                <React.Fragment key={stepKey}>
                  <div
                    className={`flex items-center justify-center w-8 h-8 rounded-full border ${
                      index <= activeStepIndex
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-border text-muted-foreground'
                    }`}
                  >
                    {index + 1}
                  </div>
                  {index < STEP_ORDER.length - 1 && (
                    <div
                      className={`flex-1 h-px ${
                        index < activeStepIndex ? 'bg-primary' : 'bg-border'
                      }`}
                    />
                  )}
                </React.Fragment>
              ))}
            </div>
            <div className="flex justify-between text-xs uppercase tracking-wide text-muted-foreground">
              {STEP_ORDER.map((stepKey) => (
                <span
                  key={stepKey}
                  className={`flex-1 text-center ${
                    step === stepKey ? 'text-primary font-semibold' : ''
                  }`}
                >
                  {stepLabels[stepKey]}
                </span>
              ))}
            </div>
          </div>

          {errors.general && (
            <div className="bg-error/10 border border-error/20 text-error rounded-lg p-4 flex items-start space-x-3">
              <Icon name="AlertCircle" size={20} className="mt-0.5" />
              <div>
                <p className="text-sm font-semibold">Something went wrong</p>
                <p className="text-sm mt-1 text-error/80">{errors.general}</p>
              </div>
            </div>
          )}

          {statusMessage && (
            <div className="bg-success/10 border border-success/20 text-success rounded-lg p-4 flex items-start space-x-3">
              <Icon name="CheckCircle" size={20} className="mt-0.5" />
              <div>
                <p className="text-sm font-semibold">All set</p>
                <p className="text-sm mt-1 text-success/80">{statusMessage}</p>
              </div>
            </div>
          )}

          <div className="space-y-8">
            {renderStepContent()}
            {step !== 'success' && (
              <div className="text-xs text-muted-foreground text-center">
                Need help? Contact your system administrator or manager for assistance.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PasswordResetPage;
